import type { Group } from "building-types/groups/group";
import type { SystemParameter } from "building-types/options/parameter";
import { ParameterType } from "building-types/options/parameter";
import type {
  CommunityParameterValues,
  LotSpecificParameterValues,
  MasterPlanParameterValues,
  RoomParameterValues,
  ViewportParameterValues,
} from "building-types/systemParameterTypes";
import {
  type BuildingParameterValues,
  type DateParameterValues,
  type HomePlanParameterValues,
  type SheetParameterValues,
  type SystemParameterId,
  type SystemParameterValues,
  type SystemParameterValuesFromDoc,
  type BuilderParameterValues,
} from "building-types/systemParameterTypes";
import type { RWTable, Table } from "common/data/table";
import { objectTypedKeys } from "common/utils/objectTypedKeys";
import { docEntityHandleHash } from "./entity/core/handleHash";

export const UNDEFINED_PARAMETER_VALUE = "-";

const VIEWPORT_SCALE_DEFAULT = "Not to scale";

type ParametersForType<T> = {
  readonly [P in keyof T]: SystemParameter;
};

function systemParameterIdToName(id: SystemParameterId): string {
  return id[0].toUpperCase() + id.slice(1);
}

let count = 0;
function textSystemParameter(id: SystemParameterId, defaultValue?: string): SystemParameter {
  return {
    entityType: "SYSTEM_PARAMETER",
    sortIndex: count++,
    type: ParameterType.TEXT,
    id,
    defaultValue,
    name: systemParameterIdToName(id),
  };
}

function urlSystemParameter(id: SystemParameterId, defaultValue?: string): SystemParameter {
  return {
    entityType: "SYSTEM_PARAMETER",
    sortIndex: count++,
    type: ParameterType.URL,
    id,
    defaultValue,
    name: systemParameterIdToName(id),
  };
}

export function viewportParameters({
  viewportName,
  viewportNumber,
  viewportDescription,
  viewportScale = VIEWPORT_SCALE_DEFAULT,
}: Partial<ViewportParameterValues> = {}): ParametersForType<ViewportParameterValues> {
  return {
    viewportName: textSystemParameter("viewportName", viewportName),
    viewportNumber: textSystemParameter("viewportNumber", viewportNumber),
    viewportDescription: textSystemParameter("viewportDescription", viewportDescription),
    viewportScale: textSystemParameter("viewportScale", viewportScale),
  };
}

export const ViewportParameters = viewportParameters();

export const SheetParameters: ParametersForType<SheetParameterValues> = {
  currentSheetNumber: textSystemParameter("currentSheetNumber"),
  currentSheetNumberPrefix: textSystemParameter("currentSheetNumberPrefix"),
  currentSheetName: textSystemParameter("currentSheetName"),
};

export const DateParameters: ParametersForType<DateParameterValues> = {
  currentDayNumber: textSystemParameter("currentDayNumber", "DD"),
  currentDay: textSystemParameter("currentDay", "DAY"),
  currentMonthNumber: textSystemParameter("currentMonthNumber", "MM"),
  currentMonth: textSystemParameter("currentMonth", "MONTH"),
  currentYear: textSystemParameter("currentYear", "YYYY"),
  currentTime: textSystemParameter("currentTime", "HH:MM:SS"),
  currentTimeZone: textSystemParameter("currentTimeZone", "TZ"),
};

export const BuildingParameters: ParametersForType<BuildingParameterValues> = {
  buildingBedroomCount: textSystemParameter("buildingBedroomCount"),
  buildingBathCount: textSystemParameter("buildingBathCount"),
  buildingConditionedArea: textSystemParameter("buildingConditionedArea"),
  buildingUnconditionedArea: textSystemParameter("buildingUnconditionedArea"),
  buildingGarageArea: textSystemParameter("buildingGarageArea"),
  buildingFrontPorchPorticoArea: textSystemParameter("buildingFrontPorchPorticoArea"),
  buildingRearPorchArea: textSystemParameter("buildingRearPorchArea"),
  buildingCoveredPatioArea: textSystemParameter("buildingCoveredPatioArea"),
  buildingUncoveredPatioArea: textSystemParameter("buildingUncoveredPatioArea"),
  buildingGarageCount: textSystemParameter("buildingGarageCount"),
  buildingStoryCount: textSystemParameter("buildingStoryCount"),
  buildingWidth: textSystemParameter("buildingWidth"),
  buildingDepth: textSystemParameter("buildingDepth"),
  buildingHeight: textSystemParameter("buildingHeight"),
};

const BuilderParameters: ParametersForType<BuilderParameterValues> = {
  builderName: textSystemParameter("builderName"),
  builderAddress: textSystemParameter("builderAddress"),
  builderCity: textSystemParameter("builderCity"),
  builderState: textSystemParameter("builderState"),
  builderZip: textSystemParameter("builderZip"),
};

export function roomParameters({
  roomArea,
  roomCeilingHeight,
  roomDimensions,
  roomFloorMaterial,
  roomReportedName,
  roomTagDetails,
  roomType,
  roomBathroomCount,
  roomBedroomCount,
  roomGarageCount,
}: Partial<RoomParameterValues> = {}): ParametersForType<RoomParameterValues> {
  return {
    roomArea: textSystemParameter("roomArea", roomArea),
    roomCeilingHeight: textSystemParameter("roomCeilingHeight", roomCeilingHeight),
    roomDimensions: textSystemParameter("roomDimensions", roomDimensions),
    roomReportedName: textSystemParameter("roomReportedName", roomReportedName),
    roomFloorMaterial: textSystemParameter("roomFloorMaterial", roomFloorMaterial),
    roomTagDetails: textSystemParameter("roomTagDetails", roomTagDetails),
    roomType: textSystemParameter("roomType", roomType),
    roomBedroomCount: textSystemParameter("roomBedroomCount", roomBedroomCount),
    roomBathroomCount: textSystemParameter("roomBathroomCount", roomBathroomCount),
    roomGarageCount: textSystemParameter("roomGarageCount", roomGarageCount),
  };
}

export const RoomParameters: ParametersForType<RoomParameterValues> = roomParameters();

export const ConfigParameters: ParametersForType<
  HomePlanParameterValues & CommunityParameterValues & LotSpecificParameterValues & MasterPlanParameterValues
> = {
  homePlanName: textSystemParameter("homePlanName"),
  homePlanSetId: textSystemParameter("homePlanSetId"),
  homePlanStatus: textSystemParameter("homePlanStatus"),
  homePlanPriceBase: textSystemParameter("homePlanPriceBase"),
  homePlanPriceOptions: textSystemParameter("homePlanPriceOptions"),
  homePlanPriceDiscount: textSystemParameter("homePlanPriceDiscount"),
  homePlanPriceTotal: textSystemParameter("homePlanPriceTotal"),
  homePlanLotPremium: textSystemParameter("homePlanLotPremium"),
  homePlanShowroomURL: urlSystemParameter("homePlanShowroomURL"),

  masterPlanName: textSystemParameter("masterPlanName"),

  communityPlanName: textSystemParameter("communityPlanName"),
  currentLocationName: textSystemParameter("currentLocationName"),
  companyName: textSystemParameter("companyName"),
  regionName: textSystemParameter("regionName"),
  divisionName: textSystemParameter("divisionName"),
  communityName: textSystemParameter("communityName"),
  phaseName: textSystemParameter("phaseName"),
  productLineName: textSystemParameter("productLineName"),
  multiunitName: textSystemParameter("multiunitName"),
  communityGeneralNotes: textSystemParameter("communityGeneralNotes"),
  communityGovernmentCodeAndStandards: textSystemParameter("communityGovernmentCodeAndStandards"),
  communityBuildingCodeAnalysisDesignCriteria: textSystemParameter("communityBuildingCodeAnalysisDesignCriteria"),
  customerName: textSystemParameter("customerName"),
  lotNumber: textSystemParameter("lotNumber"),
  lotAddress: textSystemParameter("lotAddress"),
  blockName: textSystemParameter("blockName"),
};

export const SystemParametersFromDoc: ParametersForType<SystemParameterValuesFromDoc> = {
  ...BuilderParameters,
  ...SheetParameters,
  ...ViewportParameters,
  ...DateParameters,
  ...ConfigParameters,
  ...RoomParameters,
};

export const SystemParameters: ParametersForType<SystemParameterValues> = {
  ...SystemParametersFromDoc,
  ...BuildingParameters,
};

let groupSortIndex = 0;

function systemParameterGroup(name: string, id: string): Group {
  return {
    entityType: "GROUP",
    name,
    id,
    sortIndex: groupSortIndex++,
    isBuiltIn: true,
  };
}

export const systemParametersGroups = [
  systemParameterGroup("Sheet Information", "sheetParametersGroup"),
  systemParameterGroup("Current Date", "currentDateParametersGroup"),
  systemParameterGroup("Active Model Information", "activeModelParametersGroup"),
  systemParameterGroup("Builder Information", "builderInformationGroup"),
  systemParameterGroup("Config Information", "configParametersGroup"),
  systemParameterGroup("Viewport Information", "viewportInformationGroup"),
  systemParameterGroup("Room Information", "roomInformationGroup"),
] as const satisfies Group[];

export type SystemParameterGroupId = (typeof systemParametersGroups)[number]["id"];

export const systemParametersGroupsTable: Table<Group> = systemParametersGroups.reduce((acc, group) => {
  acc[group.id] = group;
  return acc;
}, {} as RWTable<Group>);

const systemParametersEntityGroupMapping: Record<SystemParameterGroupId, SystemParameterId[]> = {
  sheetParametersGroup: objectTypedKeys(SheetParameters).sort(),
  currentDateParametersGroup: objectTypedKeys(DateParameters).sort(),
  activeModelParametersGroup: objectTypedKeys(BuildingParameters).sort(),
  builderInformationGroup: objectTypedKeys(BuilderParameters).sort(),
  configParametersGroup: objectTypedKeys(ConfigParameters).sort(),
  viewportInformationGroup: objectTypedKeys(ViewportParameters).sort(),
  roomInformationGroup: objectTypedKeys(RoomParameters).sort(),
};

function makeSystemParametersEntityGroups(
  mapping: Record<SystemParameterGroupId, SystemParameterId[]>,
): Table<Group["id"]> {
  const entityGroups: RWTable<Group["id"]> = {};
  for (const groupId in mapping) {
    for (const entityId of mapping[groupId]) {
      entityGroups[docEntityHandleHash({ type: "SYSTEM_PARAMETER", id: entityId })] = groupId;
    }
  }
  return entityGroups;
}

export const systemParametersEntityGroups: Table<Group["id"]> = makeSystemParametersEntityGroups(
  systemParametersEntityGroupMapping,
);
